import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader2, AlertTriangle, Download as DownloadIcon } from 'lucide-react';
import type { CreditBlockFile } from '../../types';
import { keycloakService } from '../../services/keycloakService';

interface Props {
  file: CreditBlockFile | null;
  onClose: () => void;
}

/**
 * In-app viewer for files stored in the Files tab.
 *
 * PDFs render in a sandboxed iframe using the browser's built-in PDF viewer —
 * which prompts for the password on encrypted PDFs (no extra dependency).
 * JSON pretty-prints inline.
 * Other mime types offer a download fallback.
 *
 * We fetch bytes from the download URL once and convert to a blob URL so
 * (a) encrypted PDFs survive the redirect chain and (b) the iframe src
 * doesn't expose a short-lived presigned URL in the address bar.
 */
const FileViewerModal: React.FC<Props> = ({ file, onClose }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const kind: 'pdf' | 'json' | 'image' | 'text' | 'other' = useMemo(() => {
    if (!file) return 'other';
    const name = (file.fileName ?? '').toLowerCase();
    const ct = (file.fileType ?? '').toLowerCase();
    if (ct.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
    if (ct.includes('json') || name.endsWith('.json')) return 'json';
    if (ct.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(name)) return 'image';
    if (ct.startsWith('text/') || /\.(txt|log|md|csv)$/i.test(name)) return 'text';
    return 'other';
  }, [file]);

  useEffect(() => {
    if (!file) return;
    const controller = new AbortController();
    setErr(null);
    setBlobUrl(null);
    setRawText(null);
    setLoading(true);

    (async () => {
      try {
        const src = file.downloadUrl;
        if (!src) {
          throw new Error('This file has no download URL.');
        }
        // Rewrite absolute Gobodo URLs to go through the /gobodo-api dev
        // proxy (same-origin, avoids CORS) and attach the Keycloak Bearer
        // token — Gobodo's file endpoints require authenticated access.
        const gobodoRe = /^https?:\/\/[^/]*gobodo[^/]*(\/.*)?$/i;
        const m = src.match(gobodoRe);
        const fetchUrl = m ? `/gobodo-api${m[1] ?? ''}` : src;
        const token = keycloakService.getToken();
        const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
        // eslint-disable-next-line no-console
        console.log('[FileViewer] fetching', fetchUrl, 'for', file.fileName, 'origUrl:', src);
        const resp = await fetch(fetchUrl, { signal: controller.signal, headers });
        if (!resp.ok) throw new Error(`Fetch failed: HTTP ${resp.status} on ${fetchUrl}`);
        const contentType = resp.headers.get('content-type') ?? file.fileType ?? '';
        if (kind === 'json' || kind === 'text') {
          const text = await resp.text();
          // Pretty-print JSON if possible
          if (kind === 'json') {
            try {
              setRawText(JSON.stringify(JSON.parse(text), null, 2));
            } catch {
              setRawText(text);
            }
          } else {
            setRawText(text);
          }
        } else {
          const buf = await resp.arrayBuffer();
          const mime = contentType || (kind === 'pdf' ? 'application/pdf' : 'application/octet-stream');
          const blob = new Blob([buf], { type: mime });
          setBlobUrl(URL.createObjectURL(blob));
        }
      } catch (e) {
        if ((e as { name?: string }).name === 'AbortError') return;
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [file, kind]);

  // Revoke the blob URL when it changes or the modal closes
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!file) return null;

  const isEncrypted = /pdf-protected/i.test(file.fileName ?? '');

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{file.fileName}</h3>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {file.category} · {file.fileType || 'unknown type'} · {Math.round((file.fileSize ?? 0) / 1024).toLocaleString()} KB
              {isEncrypted && <span className="ml-2 inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">🔒 encrypted — prompt will ask for last-4 SSN</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            {file.downloadUrl && (
              <a
                href={file.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline"
              >
                <DownloadIcon className="h-4 w-4" /> Download
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              aria-label="Close viewer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          )}
          {err && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-start gap-2 text-sm text-red-700 max-w-md p-4">
                <AlertTriangle className="h-5 w-5 mt-0.5" />
                <div>
                  <div className="font-medium">Could not load file</div>
                  <div>{err}</div>
                </div>
              </div>
            </div>
          )}
          {!loading && !err && kind === 'pdf' && blobUrl && !isEncrypted && (
            <iframe src={blobUrl} title={file.fileName} className="w-full h-full border-0" />
          )}
          {!loading && !err && kind === 'pdf' && blobUrl && isEncrypted && (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <div className="max-w-md text-center space-y-3">
                <div className="text-4xl">🔒</div>
                <div className="font-medium text-gray-900 dark:text-gray-100">Encrypted PDF</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Chrome's built-in viewer won't prompt for passwords inside an iframe, so open the file
                  in a new tab. Your browser will ask for the password (last 4 of SSN).
                </div>
                <button
                  type="button"
                  onClick={() => window.open(blobUrl, '_blank', 'noopener,noreferrer')}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  Open PDF in new tab
                </button>
              </div>
            </div>
          )}
          {!loading && !err && kind === 'image' && blobUrl && (
            <div className="w-full h-full flex items-center justify-center p-4">
              <img src={blobUrl} alt={file.fileName} className="max-w-full max-h-full object-contain" />
            </div>
          )}
          {!loading && !err && (kind === 'json' || kind === 'text') && rawText !== null && (
            <pre className="w-full h-full overflow-auto p-4 text-xs font-mono bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-all">
              {rawText}
            </pre>
          )}
          {!loading && !err && kind === 'other' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-sm text-gray-600 max-w-md p-4 text-center">
                Inline preview is not available for this file type. Use the Download link above.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileViewerModal;
